import {SelectButton} from "primereact/selectbutton";
import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";

import ErrorIndicator from "../../component/ErrorIndicator";
import LoadingIndicator from "../../component/LoadingIndicator";
import MapLayerUnavailableHint from "../../component/MapLayerUnavailableHint";
import MissingPermissionIndicator from "../../component/MissingPermissionIndicator";
import OpenLayersMap from "../../component/OpenLayersMap";
import {useRevaTranslation} from "../../hooks/languageHooks";
import {useMapSettings} from "../../hooks/useMapSettings";
import {usePdfPreview} from "../../hooks/usePdfPreview";
import {selectAddressData} from "../../redux/addressSlice";
import {queryLocationInfoAtCoordinateAsync} from "../../redux/locationInfoThunk";
import {LocationPdfType} from "../../redux/pdfConstants";
import {
    LAYER_WOHNLAGEN_MAKRO,
    LAYER_WOHNLAGEN_MIKRO_DECORATED_BACKGROUND,
    LAYER_WOHNLAGEN_MIKRO_DECORATED_TOP,
    selectCapabilities,
    selectError,
    selectLoading,
} from "../../redux/wmtsSlice";
import {
    LayerFactory,
    MacroLocationClasses,
    MicroMacroLocationIndex,
    MicroMacroNoteCard,
    resolutionFromScale,
} from "../../util/maps";
import {qualitiesPrimaryMacro, tooltipHandler} from "../../util/mapTooltipUtil";
import {PERMISSION_RULE_LOCATION_MACRO} from "../../util/permissions";
import PersistenceFinalMessage from "../object/PersistenceFinalMessage";
import SnapshotMessage from "../object/SnapshotMessage";

const LAYERS = [
    {title: "Makro Klassen", key: "location_macro", style: "reva:wohnlage_makro"},
    {title: "Makro Index", key: "location_macroindex", style: "reva:wohnlageindex_makro"},
];

function MacroLegend({showIndex, setShowIndex}) {
    const {t} = useTranslation();
    const options = [
        {label: t("location.themeClasses"), value: 0},
        {label: t("location.themeIndex"), value: 1},
    ];
    return (
        <>
            <div className="flex items-center gap-2 mb-4">
                <h2 className="m-0">{t("location.view")}:</h2>
                <SelectButton
                    value={showIndex}
                    options={options}
                    onChange={(e) => e.value != null && setShowIndex(e.value)}
                    allowEmpty={false}
                />
            </div>
            {showIndex ? <MicroMacroLocationIndex /> : <MacroLocationClasses />}
        </>
    );
}

function MacroLocationMap() {
    const {t, language} = useRevaTranslation();
    const addressData = useSelector(selectAddressData);
    const dispatch = useDispatch();
    const [showIndex, setShowIndex] = useState(0);
    const getMicroMacroData = (coordinate) => dispatch(queryLocationInfoAtCoordinateAsync(coordinate, "microMacro"));
    const {initialView, onMapMoveEnd} = useMapSettings("macro_map_settings");
    const pdfPreview = usePdfPreview(LocationPdfType.MACRO_LOCATION);

    /** @type {SelectCapabilitiesData|null} */
    const capabilities = useSelector(selectCapabilities);

    const backgroundLayerCapabilities = capabilities?.[LAYER_WOHNLAGEN_MIKRO_DECORATED_BACKGROUND];
    const topLayerCapabilities = capabilities?.[LAYER_WOHNLAGEN_MIKRO_DECORATED_TOP];

    if (!backgroundLayerCapabilities || !addressData) return null;

    const getTooltipText = (locationData) => {
        const {location_index_macro: index, location_category_macro: macro} = locationData;
        const qualityKey = qualitiesPrimaryMacro[macro];
        if (!macro || !qualityKey || index == null) {
            return;
        }
        return t("location.macro.tooltip", {index, quality: t(qualityKey)});
    };

    const layers = LAYERS.map((l) =>
        LayerFactory.createGeoserverWmsLayer(LAYER_WOHNLAGEN_MAKRO, addressData, l.style, l.key, undefined, {
            title: l.title,
            type: "base",
            visible: LAYERS[showIndex].title === l.title,
        }),
    );

    /**
     * Map Model from address.
     * @type {MapModel}
     */
    const mapModel = {
        view: {
            lat: initialView?.lat ?? addressData.lat,
            lon: initialView?.lon ?? addressData.lon,
            // Start auf rundem Maßstab 1:300.000, sofern keine gespeicherte Ansicht existiert
            ...(initialView?.zoom != null
                ? {zoom: initialView.zoom}
                : {resolution: resolutionFromScale(300000, addressData.lat)}),
            resetCenterOnChange: true,
        },
        layers: [
            LayerFactory.createGeoserverWmtsLayer(
                LAYER_WOHNLAGEN_MIKRO_DECORATED_BACKGROUND,
                addressData,
                undefined,
                "location_micro_background",
                undefined,
                {
                    ...backgroundLayerCapabilities,
                },
            ),
            ...layers,
            LayerFactory.createGeoserverWmtsLayer(
                LAYER_WOHNLAGEN_MIKRO_DECORATED_TOP,
                addressData,
                undefined,
                "location_micro_top",
                undefined,
                {
                    ...topLayerCapabilities,
                },
            ),
            LayerFactory.createMarkerLayer(addressData.address, language),
            LayerFactory.createMarkerLayer([], language, "tooltip-markers"),
        ],
        eventHandlers: {
            click_event: (evt, map) => tooltipHandler(evt, map, getMicroMacroData, getTooltipText),
            hover_feature_event: (evt, map) => tooltipHandler(evt, map, getMicroMacroData, getTooltipText),
            moveend_event: onMapMoveEnd,
        },
        scaleLine: {enabled: true},
    };

    return (
        <div className="flex-1 relative">
            <div className="OpenLayersMapBackdrop w-full h-full">
                <OpenLayersMap
                    model={mapModel}
                    legend={<MacroLegend showIndex={showIndex} setShowIndex={setShowIndex} />}
                    legendOptions={{visible: true}}
                    pdfPreview={pdfPreview}
                />
            </div>
        </div>
    );
}

/**
 *
 * @returns {React.JSX.Element}
 */
export function TabMacroLocation() {
    const {t} = useTranslation();
    const capabilitiesLoading = useSelector(selectLoading);
    const capabilitiesError = useSelector(selectError);

    return (
        <div className="flex flex-col h-full">
            <PersistenceFinalMessage />
            <SnapshotMessage />
            <ErrorIndicator errorObject={capabilitiesError} titleI18nKey="error.error" />
            <LoadingIndicator loading={capabilitiesLoading} />
            <MissingPermissionIndicator permissionRule={PERMISSION_RULE_LOCATION_MACRO}>
                <div className="MapSplit flex-1 min-h-0">
                    {/* Linke Spalte in Scroll-Containment kapseln (wie Klimarisiken-Tab), damit
                        lange Texte die Flex-Row NICHT in die Höhe strecken und die Karte immer
                        gleich hoch bleibt; der linke Bereich bekommt bei Bedarf einen Scrollbalken. */}
                    <div className="MapSplitLeft flex flex-col">
                        <div className="flex-1 relative">
                            <div className="absolute w-full h-full overflow-auto">
                                <MicroMacroNoteCard specificNote={t("location.macro.note")} />
                            </div>
                        </div>
                    </div>
                    <div className="MapSplitRight flex flex-col">
                        <MapLayerUnavailableHint
                            layers={[LAYER_WOHNLAGEN_MIKRO_DECORATED_BACKGROUND, LAYER_WOHNLAGEN_MIKRO_DECORATED_TOP]}
                            overlay={false}
                        />
                        <MacroLocationMap />
                    </div>
                </div>
            </MissingPermissionIndicator>
        </div>
    );
}

export default TabMacroLocation;
